/**
 * Events/Webhooks API Mappers
 *
 * Verified against:
 * - dataProtect source: /components/events/*.js, /shared/redux/reducers/events.js
 * - Backend: transformer_api (data service)
 */
import type {
  CreateObjectTypeRequest,
  CreateWebhookSubscriptionRequest,
  ObjectType,
  TriggerSubscription,
  UpdateObjectTypeRequest,
  WebhookSubscription,
} from '../types';

/**
 * Transform object types list response and filter system objects
 * Verified against: dataProtect/src/shared/redux/reducers/events.js:L24-27
 * Filters out: ceds.* objects and lingk.api.bulkupload
 * Maps backend snake_case to frontend camelCase
 */

interface ObjectTypeApiResponse {
  name: string;
  private: boolean;
  description: string;
  created_at: string;
  created_by: string;
}

interface ObjectTypesApiWrapper {
  data?: ObjectTypeApiResponse[];
}

export const transformGetObjectTypesResponse = (response: ObjectTypesApiWrapper): ObjectType[] => {
  const data = Array.isArray(response?.data) ? response.data : [];

  return data
    .filter((f) => f.name?.substr(0, 5) !== 'ceds.' && f.name !== 'lingk.api.bulkupload')
    .map((obj) => ({
      name: obj.name,
      private: obj.private,
      description: obj.description,
      createdAt: obj.created_at, // Backend: snake_case → Frontend: camelCase
      createdBy: obj.created_by, // Backend: snake_case → Frontend: camelCase
    }));
};

/**
 * Transform create object type request
 * Verified against: dataProtect/src/components/events/newWebhook.js:L103
 * Backend expects: { name: string, private: boolean }
 */
export const transformCreateObjectTypeRequest = (data: CreateObjectTypeRequest) => {
  return {
    name: data.name,
    private: data.private,
  };
};

/**
 * Transform update object type request
 * Verified against: EventsObjectFormPage.tsx:L113-117
 * Backend expects: { name: string, private: boolean } - exact same structure
 */
export const transformUpdateObjectTypeRequest = (data: UpdateObjectTypeRequest) => {
  return {
    name: data.name,
    private: data.private,
  };
};

/**
 * Transform create webhook subscription request
 * Verified against: EventsSubscriptionFormPage.tsx:L154-158
 * Backend expects: { objectTypeFilter: string, verbFilter: string, deliveryUrl: string }
 * Pass through as-is (no transformation needed)
 */
export const transformCreateWebhookSubscriptionRequest = (
  data: CreateWebhookSubscriptionRequest
) => {
  return {
    objectTypeFilter: data.objectTypeFilter,
    verbFilter: data.verbFilter,
    deliveryUrl: data.deliveryUrl,
  };
};

/**
 * Transform webhook subscriptions list response
 * Backend returns array directly or {data: [...]}
 * Add id field from subscriptionGuid for easier keying
 */
export const transformGetWebhookSubscriptionsResponse = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  response: any
): WebhookSubscription[] => {
  let data: WebhookSubscription[];
  if (Array.isArray(response)) {
    data = response;
  } else if (Array.isArray(response?.data)) {
    const { data: responseData } = response;
    data = responseData;
  } else {
    data = [];
  }

  return data.map((sub: WebhookSubscription) => ({
    ...sub,
    id: sub.data?.subscriptionGuid || `${sub.baseId}-${sub.environmentId}`,
  }));
};

/**
 * Transform trigger subscriptions list response
 * Backend returns array directly or {data: [...]}
 * Add id field from BaseId-EnvironmentId for easier keying
 */
export const transformGetTriggerSubscriptionsResponse = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  response: any
): TriggerSubscription[] => {
  let data: TriggerSubscription[];
  if (Array.isArray(response)) {
    data = response;
  } else if (Array.isArray(response?.data)) {
    const { data: responseData } = response;
    data = responseData;
  } else {
    data = [];
  }

  return data.map((sub: TriggerSubscription) => ({
    ...sub,
    id: `${sub.BaseId}-${sub.EnvironmentId}`,
  }));
};
